/**
 * React.lazy with resilience to stale-chunk failures after a deploy (W0-3 / RT-2).
 *
 * The dashboard code-splits ~24 pages with dynamic import(). When a new build is
 * deployed while a user has the app open, their already-loaded index.html points
 * at the PREVIOUS content-hashed bundles. Navigating to a not-yet-loaded lazy page
 * then requests an old assets/index-<hash>.js which — depending on the deploy —
 * may 404, surfacing as a blank page (ChunkLoadError). The sync now retains old
 * chunks (scripts/sync-portal.sh), and this wrapper is the client-side belt-and-
 * braces: on an import failure it forces a single full reload, which fetches the
 * current index.html and its matching chunk set.
 *
 * A sessionStorage flag makes the reload fire at most once per session per chunk,
 * so a genuinely broken build cannot cause an infinite reload loop — after one
 * reload the error propagates to the nearest error boundary instead.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type Factory<T extends ComponentType<any>> = () => Promise<{ default: T }>;

export async function loadWithReload<T extends ComponentType<any>>(
  factory: Factory<T>,
  key: string,
): Promise<{ default: T }> {
  const flag = `lazyReload:${key}`;
  try {
    const mod = await factory();
    // Loaded cleanly — clear any prior reload marker so a FUTURE deploy can also
    // trigger a one-shot reload for this chunk.
    try { window.sessionStorage.removeItem(flag); } catch { /* storage disabled */ }
    return mod;
  } catch (err) {
    let alreadyReloaded = false;
    try { alreadyReloaded = window.sessionStorage.getItem(flag) === '1'; } catch { /* ignore */ }
    if (!alreadyReloaded) {
      try { window.sessionStorage.setItem(flag, '1'); } catch { /* ignore */ }
      window.location.reload();
      // Nothing should render before the reload takes effect.
      return new Promise<{ default: T }>(() => { /* never resolves */ });
    }
    // Reload already happened this session and it STILL failed — this is a real
    // error, not a stale chunk. Let the error boundary handle it.
    throw err;
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: Factory<T>,
  key: string,
): LazyExoticComponent<T> {
  return lazy(() => loadWithReload(factory, key));
}
